// Audit P2 portal-order-detail — a 5-step horizontal stepper that
// communicates where the rental sits in its lifecycle without needing
// any audit-log rows. Reuses the `orders.status` value already on the
// payload. Exception states (cancelled / delivery_failed / capture_failed)
// fall back to a single-line notice rather than rendering an awkward
// stepper that doesn't reflect where the order died.

const STEPS = ["Reserved", "Dispatched", "Delivered", "On rent", "Returned"] as const;

const STATUS_INDEX: Record<string, number> = {
  // 0 — pre-delivery / drafting
  reserved: 0,
  pending_payment: 0,
  pending_procurement: 0,
  pending_id_capture: 0,
  // 1 — driver dispatched, equipment in transit
  dispatched: 1,
  // 2 — equipment on customer's site
  delivered: 2,
  // 3 — active rental, customer using equipment
  on_rent: 3,
  // 4 — equipment back at yard
  returned: 4,
  closed: 4
};

const EXCEPTION_LABELS: Record<string, string> = {
  cancelled: "Order cancelled",
  delivery_failed: "Delivery failed — call (303) 435-4328",
  capture_failed: "Payment failed — call (303) 435-4328"
};

export function LifecycleStepper({ status }: { status: string }): JSX.Element {
  const exceptionLabel = EXCEPTION_LABELS[status];
  if (exceptionLabel) {
    return (
      <div
        className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm font-semibold text-danger"
        data-testid="lifecycle-exception"
        role="status"
      >
        {exceptionLabel}
      </div>
    );
  }

  const currentIndex = STATUS_INDEX[status] ?? 0;
  const currentLabel = STEPS[currentIndex];
  return (
    <div data-testid="lifecycle-stepper-wrap">
      {/* Audit P2 — on narrow viewports the 5-label stepper used to
          overflow horizontally, hiding "On rent" / "Returned" behind a
          scroll. We surface the current step as a caption above the
          row so even when individual labels are hidden the customer
          knows where the order sits. */}
      <p className="mb-1 text-xs font-semibold text-brand-black sm:hidden" data-testid="lifecycle-current-label">
        Currently: <span className="font-bold">{currentLabel}</span>
      </p>
      <ol
        className="flex items-center gap-1"
        aria-label="Order lifecycle"
        data-testid="lifecycle-stepper"
      >
        {STEPS.map((label, idx) => {
          const state =
            idx < currentIndex ? "complete" : idx === currentIndex ? "current" : "upcoming";
          return (
            <li
              key={label}
              data-testid="lifecycle-step"
              data-state={state}
              aria-current={state === "current" ? "step" : undefined}
              className="flex flex-1 items-center gap-2 first:flex-initial last:flex-initial"
            >
              <Dot state={state} index={idx} />
              <span
                className={
                  // Hide labels on < sm; the caption above carries the
                  // human-readable step name for narrow viewports.
                  "hidden whitespace-nowrap text-xs font-medium sm:inline " +
                  (state === "current"
                    ? "text-brand-black"
                    : state === "complete"
                      ? "text-brand-red"
                      : "text-zinc-400")
                }
              >
                {label}
              </span>
              {idx < STEPS.length - 1 ? <Connector state={state} /> : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Dot({ state, index }: { state: "complete" | "current" | "upcoming"; index: number }) {
  const base = "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold transition";
  if (state === "complete") {
    return (
      <span aria-hidden="true" className={`${base} bg-brand-red text-brand-white`}>
        ✓
      </span>
    );
  }
  if (state === "current") {
    return (
      <span
        aria-hidden="true"
        className={`${base} bg-brand-white text-brand-red ring-2 ring-brand-red`}
      >
        {index + 1}
      </span>
    );
  }
  return (
    <span aria-hidden="true" className={`${base} bg-zinc-100 text-zinc-400 ring-1 ring-zinc-200`}>
      {index + 1}
    </span>
  );
}

function Connector({ state }: { state: "complete" | "current" | "upcoming" }): JSX.Element {
  const tone =
    state === "complete" ? "bg-brand-red" : state === "current" ? "bg-brand-red/40" : "bg-zinc-200";
  return <span aria-hidden="true" className={`h-px flex-1 ${tone}`} />;
}
